import { useQuery } from "@apollo/client/react";
import dayjs from "dayjs";
import type { SortDirection } from "../../../models/SortDirection";
import GET_CLIENT_INVOICES from "../../queries/invoices/getClientInvoices";
import type { InvoiceResponseModel } from "../../../models/InvoiceModels";
import type { InvoiceTag } from "../../../models/InvoiceTags";
import type { ClientFilterType } from "./useClientAssessmentInvoices";
import type {
  InvoiceAssessmentStatus,
  InvoiceValidationStatus,
} from "../../../models/InvoiceStatusModel";

type InvoiceSortBy =
  | "CREATED_AT"
  | "DUE_DATE"
  | "INVOICE_DATE"
  | "SENT_FROM_ERP_TO_RECIPIENT_AT"
  | "INVOICE_NUMBER";

interface UseClientInvoicesOptions {
  sortBy?: InvoiceSortBy;
  sortDirection?: SortDirection;
  invoiceDateFrom?: Date;
  invoiceDateTo?: Date;
  dueDateFrom?: Date;
  dueDateTo?: Date;
  invoiceStatuses?: string[];
  validationStatuses?: InvoiceValidationStatus[];
  assessmentStatuses?: InvoiceAssessmentStatus[];
  amountFrom?: number;
  amountTo?: number;
  tags?: InvoiceTag[];
  skip?: number;
  take?: number;
  clientFilterType?: ClientFilterType;
}

export const useClientInvoices = (
  clientIdentifier: string,
  options: UseClientInvoicesOptions = {},
) => {
  const {
    sortBy,
    sortDirection,
    invoiceDateFrom,
    invoiceDateTo,
    dueDateFrom,
    dueDateTo,
    invoiceStatuses,
    validationStatuses,
    assessmentStatuses,
    amountFrom,
    amountTo,
    tags,
    skip,
    take,
    clientFilterType,
  } = options;

  const { data, error, loading, refetch } = useQuery<{
    invoices: InvoiceResponseModel;
  }>(GET_CLIENT_INVOICES, {
    variables: {
      clientIdentifier,
      sortBy,
      sortDirection,
      invoiceDateFrom: invoiceDateFrom
        ? dayjs(invoiceDateFrom).format("YYYY-MM-DD")
        : undefined,
      invoiceDateTo: invoiceDateTo
        ? dayjs(invoiceDateTo).format("YYYY-MM-DD")
        : undefined,
      dueDateFrom: dueDateFrom
        ? dayjs(dueDateFrom).format("YYYY-MM-DD")
        : undefined,
      dueDateTo: dueDateTo ? dayjs(dueDateTo).format("YYYY-MM-DD") : undefined,
      invoiceStatuses: invoiceStatuses,
      validationStatuses,
      assessmentStatuses,
      amountFrom,
      amountTo,
      tags,
      skip,
      take,
      clientFilterType,
    },
    errorPolicy: "all",
  });
  return { invoices: data?.invoices, error, loading, refetch };
};
